import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/providers/auth-provider";
import { getCurrentRaid } from "@/services/raid/get-current-raid";

/**
 * Hook to fetch current raid status on mount.
 * Returns the current raid ID and status if the player is in an active raid.
 */
export function useRaidStatus(
  enabled: boolean = true,
  initialData?: { raidId: string | null; status: string | null },
) {
  const { getAccessToken } = useAuth();
  const [currentRaidId, setCurrentRaidId] = useState<string | null>(
    initialData?.raidId ?? null,
  );
  const [currentRaidStatus, setCurrentRaidStatus] = useState<string | null>(
    initialData?.status ?? null,
  );

  const fetchRaidStatus = useCallback(async () => {
    const accessToken = getAccessToken();
    if (!accessToken) {
      setCurrentRaidId(null);
      setCurrentRaidStatus(null);
      return;
    }

    try {
      const response = await getCurrentRaid(accessToken);
      if (response.ok && response.data?.raid_id) {
        setCurrentRaidId(response.data.raid_id);
        setCurrentRaidStatus(response.data.raid_status || null);
      } else {
        setCurrentRaidId(null);
        setCurrentRaidStatus(null);
      }
    } catch (error) {
      console.error("Failed to fetch raid status:", error);
      setCurrentRaidId(null);
      setCurrentRaidStatus(null);
    }
  }, [getAccessToken]);

  useEffect(() => {
    if (!enabled) return;
    fetchRaidStatus();
  }, [enabled, fetchRaidStatus]);

  return { currentRaidId, currentRaidStatus, refetch: fetchRaidStatus };
}

